// FPT-23: the two notices a self-serve organisation may need, under the app bar of every app.
//
// - An admin whose mail domain is still being set up is pointed to the admin console's Get Started
//   (the DNS steps live there, never in the apps).
// - A paid plan past its first 6 months with no billing in place says so. A notice only: nothing is
//   locked (Fg.Entitlements PlanBillingState.BillingRequired).
//
// Reads GET {baseUrl}/api/entitlements (any BFF that maps it; every app already calls the Drive BFF
// for appearance). Self-contained: plain React + Tailwind, like suite-shell.

import { useEffect, useState } from "react";

/** The fields of /api/entitlements these notices read. */
export interface PlanNoticeState {
    billing?: "none" | "no-charge" | "billing-required";
    noChargeUntil?: string | null;
    mailSetup?: { state: "pending" | "verified"; adminUrl: string } | null;
}

/** What to show for a state; pure, so it is testable without a DOM. */
export function planNoticeLines(s: PlanNoticeState | null | undefined): { kind: "mail" | "billing"; text: string; href?: string; link?: string }[] {
    if (!s) return [];
    const out: { kind: "mail" | "billing"; text: string; href?: string; link?: string }[] = [];
    if (s.mailSetup && /^https:\/\//.test(s.mailSetup.adminUrl)) {
        out.push({
            kind: "mail",
            text:
                s.mailSetup.state === "verified"
                    ? "Your domain is verified. Mail is being switched on."
                    : "Mail for your organisation starts once your domain is set up.",
            href: s.mailSetup.adminUrl,
            link: s.mailSetup.state === "verified" ? "View Status" : "Finish Setup"
        });
    }
    if (s.billing === "billing-required") {
        out.push({ kind: "billing", text: "Your first 6 months are over. Everything keeps working while billing is set up." });
    }
    return out;
}

/** Fetches the notices once per sign-in. Failures show nothing: a notice never gets in the way. */
export function usePlanNotices(baseUrl: string | undefined, token: string | undefined): PlanNoticeState | null {
    const [state, setState] = useState<PlanNoticeState | null>(null);
    const signedIn = Boolean(token);
    useEffect(() => {
        if (!baseUrl || !token) return;
        let live = true;
        fetch(`${baseUrl.replace(/\/$/, "")}/api/entitlements`, {
            headers: { Accept: "application/json", Authorization: `Bearer ${token}` }
        })
            .then((r) => (r.ok ? (r.json() as Promise<PlanNoticeState>) : null))
            .then((s) => {
                if (live) setState(s);
            })
            .catch(() => undefined);
        return () => {
            live = false;
        };
        // Once per sign-in, not per token refresh.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [baseUrl, signedIn]);
    return state;
}

/** The notices, or nothing. Place directly under the app bar. */
export function PlanNotices({ baseUrl, token }: { baseUrl: string | undefined; token: string | undefined }) {
    const lines = planNoticeLines(usePlanNotices(baseUrl, token));
    if (lines.length === 0) return null;
    return (
        <div className="shrink-0">
            {lines.map((l) => (
                <div
                    key={l.kind}
                    role="status"
                    className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-amber-500/30 bg-amber-500/10 px-4 py-1.5 text-xs text-amber-800 dark:text-amber-200"
                >
                    <span>{l.text}</span>
                    {l.href ? (
                        <a href={l.href} className="font-semibold underline underline-offset-2 hover:no-underline">
                            {l.link}
                        </a>
                    ) : null}
                </div>
            ))}
        </div>
    );
}
